import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../lib/firebase/config';

/** Roles allowed to use / be assigned a 4-digit login PIN (mirror of the Cloud Function). */
export const PIN_ELIGIBLE_ROLES = ['Master Admin', 'Store Manager', 'Store Staff', 'Template'];

export function isPinEligible(roles: string[] | undefined | null): boolean {
  return !!roles?.some((r) => PIN_ELIGIBLE_ROLES.includes(r));
}

const exchangePinForTokenFn = httpsCallable<{ pin: string }, { token: string }>(
  functions,
  'exchangePinForToken'
);

const setUserLoginPinFn = httpsCallable<
  { pin: string | null; targetUid?: string },
  { ok: boolean; cleared?: boolean }
>(functions, 'setUserLoginPin');

function friendlyError(e: any): Error {
  const msg: string = e?.message || 'Something went wrong.';
  return new Error(msg);
}

/**
 * Sign in with a 4-digit PIN. Exchanges the PIN for a Firebase custom token via
 * a Cloud Function, then signs in. The normal auth state listener takes over.
 */
export async function loginWithPin(pin: string): Promise<void> {
  try {
    const res = await exchangePinForTokenFn({ pin });
    const token = res.data?.token;
    if (!token) throw new Error('Login failed. Please try again.');
    await signInWithCustomToken(auth, token);
  } catch (e) {
    throw friendlyError(e);
  }
}

/**
 * Set, change, or clear a login PIN.
 * - Omit `targetUid` (or pass your own) to change your own PIN.
 * - Master Admins can pass another user's uid.
 * - Pass `pin = null` to remove the PIN.
 */
export async function setLoginPin(pin: string | null, targetUid?: string): Promise<{ cleared?: boolean }> {
  try {
    const res = await setUserLoginPinFn({ pin, targetUid });
    return { cleared: res.data?.cleared };
  } catch (e) {
    throw friendlyError(e);
  }
}
